import React, { useEffect, useMemo, useState } from "react";
import { api, useAuth } from "@/AuthContext";
import {
  ClockClockwise, MagnifyingGlass, ArrowCounterClockwise, DownloadSimple,
  Buildings, User as UserIcon, Funnel
} from "@phosphor-icons/react";

const ACTION_COLORS = {
  create: "bg-emerald-500", update: "bg-blue-500", delete: "bg-rose-500",
  upload: "bg-violet-500", approve: "bg-emerald-600", reject: "bg-rose-600",
  resubmit: "bg-amber-500", toggle: "bg-blue-400", reassign: "bg-cyan-600",
};
const ACTION_LABEL = {
  create: "Creó", update: "Actualizó", delete: "Eliminó",
  upload: "Subió", approve: "Aprobó", reject: "Rechazó",
  resubmit: "Reenvió", toggle: "Cambió", reassign: "Reasignó",
};
const ENTITY_LABEL = {
  document: "Documento", installation: "Instalación", obligation: "Obligación",
  catalog: "Catálogo", company: "Empresa", user: "Usuario",
  activity: "Actividad", calendar_event: "Evento",
};


export default function AuditLog() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [facets, setFacets] = useState({ users: [], entities: [], actions: [] });
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    company_id: "",
    user_id: "",
    entity_type: "",
    action: "",
    from_date: "",
    to_date: "",
    q: "",
  });

  const companiesById = useMemo(() => Object.fromEntries(companies.map(c => [c.id, c])), [companies]);

  const reload = () => {
    setLoading(true);
    const params = { limit: 500 };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/audit-log", { params })
      .then(r => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/companies").then(r => setCompanies(r.data)).catch(()=>{});
    api.get("/audit-log/facets").then(r => setFacets(r.data)).catch(()=>{});
  }, []);

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filters]);

  const resetFilters = () => setFilters({ company_id:"", user_id:"", entity_type:"", action:"", from_date:"", to_date:"", q:"" });

  const exportCSV = () => {
    const headers = ["Fecha", "Empresa", "Usuario", "Acción", "Módulo", "Detalle"];
    const rows = items.map(i => [
      new Date(i.timestamp).toLocaleString("es-MX"),
      companiesById[i.active_company_id || i.company_id]?.name || "",
      i.user_name || "",
      ACTION_LABEL[i.action] || i.action,
      ENTITY_LABEL[i.entity_type] || i.entity_type,
      (i.details || "").replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `auditoria_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Trazabilidad</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Bitácora de auditoría</h1>
          <p className="text-neutral-500 mt-2">Registro de todas las operaciones con trazabilidad por empresa, usuario y módulo.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={resetFilters} disabled={activeFiltersCount===0} className="inline-flex items-center gap-2 px-4 py-2.5 border border-neutral-200 bg-white text-neutral-700 text-sm font-semibold rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="audit-reset-btn">
            <ArrowCounterClockwise size={16}/> Limpiar
          </button>
          <button onClick={exportCSV} disabled={items.length===0} className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-50" data-testid="audit-export-csv">
            <DownloadSimple size={16}/> Exportar CSV
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-neutral-200 rounded-md p-4">
        <div className="flex items-center gap-2 mb-3">
          <Funnel size={18} className="text-blue-600" weight="duotone"/>
          <h3 className="font-semibold text-sm">Filtros</h3>
          {activeFiltersCount > 0 && (
            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{activeFiltersCount} activo{activeFiltersCount!==1?'s':''}</span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">Búsqueda</label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400"/>
              <input
                value={filters.q}
                onChange={(e)=>setFilters({...filters, q:e.target.value})}
                placeholder="Detalle o usuario..."
                className="w-full pl-8 pr-3 py-2 border border-neutral-200 rounded-md text-sm"
                data-testid="audit-filter-q"
              />
            </div>
          </div>
          {(user?.role === "super_admin" || (companies?.length || 0) > 1) && (
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">Empresa</label>
              <select value={filters.company_id} onChange={(e)=>setFilters({...filters, company_id:e.target.value})} className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm bg-white" data-testid="audit-filter-company">
                <option value="">Todas</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">Usuario</label>
            <select value={filters.user_id} onChange={(e)=>setFilters({...filters, user_id:e.target.value})} className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm bg-white" data-testid="audit-filter-user">
              <option value="">Todos</option>
              {facets.users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">Módulo</label>
            <select value={filters.entity_type} onChange={(e)=>setFilters({...filters, entity_type:e.target.value})} className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm bg-white" data-testid="audit-filter-entity">
              <option value="">Todos</option>
              {facets.entities.map(e => <option key={e} value={e}>{ENTITY_LABEL[e] || e}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">Acción</label>
            <select value={filters.action} onChange={(e)=>setFilters({...filters, action:e.target.value})} className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm bg-white" data-testid="audit-filter-action">
              <option value="">Todas</option>
              {facets.actions.map(a => <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">Desde</label>
            <input type="date" value={filters.from_date} onChange={(e)=>setFilters({...filters, from_date:e.target.value})} className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm" data-testid="audit-filter-from"/>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-1.5">Hasta</label>
            <input type="date" value={filters.to_date} onChange={(e)=>setFilters({...filters, to_date:e.target.value})} className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm" data-testid="audit-filter-to"/>
          </div>
        </div>
      </div>

      {/* Tabla / Timeline */}
      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200 bg-neutral-50">
          <h3 className="font-semibold text-sm text-neutral-700">{items.length} registro{items.length!==1?'s':''}</h3>
          {loading && <span className="text-xs text-neutral-400 animate-pulse">Cargando...</span>}
        </div>
        {items.length===0 ? (
          <div className="py-16 text-center text-neutral-400" data-testid="audit-empty">
            <ClockClockwise size={42} className="mx-auto mb-2 text-neutral-300" weight="duotone"/>
            <p>Sin entradas para los filtros aplicados.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-[11px] uppercase tracking-widest text-neutral-500">
                  <th className="px-5 py-2.5 font-semibold">Fecha</th>
                  <th className="px-3 py-2.5 font-semibold">Empresa</th>
                  <th className="px-3 py-2.5 font-semibold">Usuario</th>
                  <th className="px-3 py-2.5 font-semibold">Acción</th>
                  <th className="px-3 py-2.5 font-semibold">Módulo</th>
                  <th className="px-3 py-2.5 font-semibold">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => {
                  const compId = it.active_company_id || it.company_id;
                  const compName = companiesById[compId]?.name;
                  return (
                    <tr key={it.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`audit-row-${it.id}`}>
                      <td className="px-5 py-2.5 text-xs text-neutral-500 whitespace-nowrap">
                        {new Date(it.timestamp).toLocaleString("es-MX", { dateStyle:"short", timeStyle:"short" })}
                      </td>
                      <td className="px-3 py-2.5">
                        {compName ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                            <Buildings size={11} weight="duotone"/> {compName}
                          </span>
                        ) : <span className="text-xs text-neutral-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-800">
                          <UserIcon size={12} className="text-neutral-400"/>
                          {it.user_name || <em className="text-neutral-400">desconocido</em>}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span className={`w-1.5 h-1.5 rounded-full ${ACTION_COLORS[it.action] || 'bg-neutral-400'}`}></span>
                          {ACTION_LABEL[it.action] || it.action}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded text-xs">
                          {ENTITY_LABEL[it.entity_type] || it.entity_type}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-neutral-600 max-w-md truncate" title={it.details}>{it.details}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
